import { Injectable } from '@angular/core';
import { Confirmation, ConfirmationService } from 'primeng/api';

/**
 * The application hosts a single global `<p-confirmDialog>` (app.component.html), so every
 * `ConfirmationService.confirm()` call shares one dialog component instance. PrimeNG copies each
 * confirmation's keys onto that instance and reads them back via `option()`/`hasOwnProperty`, but it
 * never clears keys that a *later* confirmation omits. As a result, the `acceptLabel` "Do not
 * discard" and `rejectLabel` "Discard" set by the unsaved-changes discard prompt (see
 * `UiConfirmService.confirmDiscardChanges`) leak into the next `confirm()` that omits them (e.g.
 * Close/Cancel Case in `case-info.component.ts`), rendering the wrong footer button labels.
 *
 * This override defaults `acceptLabel`/`rejectLabel` to `undefined` on every `confirm()` call (the
 * caller's own values, spread last, always win). Sending them present-but-`undefined` makes PrimeNG
 * overwrite the stale own-property so its label getters fall back to the built-in translation
 * default ("Yes"/"No"). Registered as the `ConfirmationService` provider in `app.module.ts`, so
 * every injector (the `UiConfirmService` wrapper and all raw `ConfirmationService` consumers) is
 * covered without any call-site changes.
 *
 * Scope note: ONLY `acceptLabel`/`rejectLabel` are defaulted. The dialog's button style classes
 * (`btn-primary`/`btn-transparent`) and icons (`pi pi-check`/`pi pi-times`) are supplied as static
 * `@Input` defaults on `<p-confirmDialog>` in `app.component.html`, which Angular applies once at
 * init and never re-applies. Injecting those keys as `undefined` would permanently clobber the
 * template default and strip styling/icons from every dialog — so they are deliberately left
 * untouched. (The discard prompt sets the button classes to the same values as the template, so
 * they never visibly leak; only the labels do.)
 */
@Injectable()
export class ResettingConfirmationService extends ConfirmationService {
  override confirm(confirmation: Confirmation): this {
    // Preserve PrimeNG's hide/no-op semantics for a null/undefined confirmation: the base service
    // emits it unchanged and the dialog hides. Spreading would otherwise turn it into a non-null
    // object that opens an empty dialog.
    if (confirmation == null) {
      return super.confirm(confirmation);
    }
    // Default the two leak-prone labels; the caller's values (spread last) always win.
    return super.confirm({
      acceptLabel: undefined,
      rejectLabel: undefined,
      ...confirmation,
    });
  }
}
